import React from 'react';
import { Page, Text, View, StyleSheet } from '@react-pdf/renderer';

/**
 * Quick Reference Guide
 * 
 * One-page cheat sheet for emergency actions
 * Designed to be posted on refrigerator or bulletin board
 */

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
    fontSize: 9,
    backgroundColor: '#ffffff',
  },
  header: {
    backgroundColor: '#dc2626',
    padding: 12,
    marginHorizontal: -30,
    marginTop: -30,
    marginBottom: 15,
  },
  headerTitle: {
    fontSize: 24,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 10,
    color: '#fecaca',
    textAlign: 'center',
    marginTop: 4,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  column: {
    width: '49%',
    marginRight: '2%',
  },
  section: {
    marginBottom: 12,
    border: '1pt solid #e5e7eb',
    borderRadius: 6,
    overflow: 'hidden',
  },
  sectionHeader: {
    padding: 6,
    backgroundColor: '#1f2937',
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionIcon: {
    fontSize: 12,
    marginRight: 6,
  },
  sectionTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
  },
  sectionBody: {
    padding: 8,
    backgroundColor: '#f9fafb',
  },
  actionItem: {
    flexDirection: 'row',
    marginBottom: 4,
    alignItems: 'flex-start',
  },
  actionNumber: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#ef4444',
    color: '#ffffff',
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    lineHeight: 14,
    marginRight: 6,
  },
  actionText: {
    fontSize: 8,
    color: '#374151',
    flex: 1,
    lineHeight: 1.4,
  },
  actionBold: {
    fontFamily: 'Helvetica-Bold',
  },
  emergencyBox: {
    backgroundColor: '#fef2f2',
    border: '2pt solid #ef4444',
    borderRadius: 6,
    padding: 10,
    marginBottom: 12,
  },
  emergencyTitle: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: '#b91c1c',
    textAlign: 'center',
    marginBottom: 8,
  },
  emergencyRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  emergencyContact: {
    alignItems: 'center',
  },
  emergencyNumber: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: '#dc2626',
  },
  emergencyLabel: {
    fontSize: 7,
    color: '#6b7280',
  },
  utilityBox: {
    backgroundColor: '#f0f9ff',
    border: '1pt solid #0ea5e9',
    borderRadius: 4,
    padding: 8,
    marginTop: 8,
  },
  utilityTitle: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: '#0369a1',
    marginBottom: 4,
  },
  utilityItem: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  utilityLabel: {
    fontSize: 7,
    color: '#6b7280',
    width: 60,
  },
  utilityValue: {
    fontSize: 7,
    color: '#1f2937',
    flex: 1,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    borderTop: '1pt solid #e5e7eb',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 7,
    color: '#9ca3af',
  },
});

interface QuickReferenceProps {
  data: {
    name?: string;
    address?: string;
    emergency_types?: string[];
    meeting_points?: {
      primary: string;
      secondary: string;
    };
    utility_shutoffs?: {
      gas?: string;
      water?: string;
      electric?: string;
    };
    local_emergency?: string;
  };
  procedures?: {
    type: string;
    immediate_actions: string[];
  }[];
}

export const QuickReference: React.FC<QuickReferenceProps> = ({ data, procedures }) => {
  // Default procedures if none provided
  const defaultProcedures = [
    {
      type: 'Earthquake',
      icon: '🌍',
      immediate_actions: [
        'DROP to the ground',
        'Take COVER under sturdy furniture',
        'HOLD ON until shaking stops',
        'Move away from windows/glass',
        'Exit building if safe after shaking',
      ],
    },
    {
      type: 'Fire',
      icon: '🔥',
      immediate_actions: [
        'ALERT everyone - yell "FIRE!"',
        'Get low, crawl under smoke',
        'Feel doors before opening',
        'Exit via nearest safe route',
        'Meet at designated location',
        'Call 911 from outside',
      ],
    },
    {
      type: 'Severe Storm/Tornado',
      icon: '🌪️',
      immediate_actions: [
        'Move to interior room/basement',
        'Stay away from windows',
        'Get under sturdy furniture',
        'Cover head and neck',
        'Wait for all-clear signal',
      ],
    },
    {
      type: 'Flood',
      icon: '🌊',
      immediate_actions: [
        'Move to higher ground immediately',
        'Never walk/drive through water',
        "Turn Around, Don't Drown",
        'Avoid bridges over fast water',
        'Disconnect electrical appliances',
      ],
    },
  ];

  const emergencyProcedures = procedures?.length ? procedures : defaultProcedures;

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>⚡ QUICK REFERENCE GUIDE</Text>
        <Text style={styles.headerSubtitle}>
          POST IN VISIBLE LOCATION • REVIEW WITH ALL FAMILY MEMBERS
        </Text>
      </View>

      {/* Emergency Numbers Box */}
      <View style={styles.emergencyBox}>
        <Text style={styles.emergencyTitle}>🚨 EMERGENCY NUMBERS</Text>
        <View style={styles.emergencyRow}>
          <View style={styles.emergencyContact}>
            <Text style={styles.emergencyNumber}>911</Text>
            <Text style={styles.emergencyLabel}>Police/Fire/Medical</Text>
          </View>
          <View style={styles.emergencyContact}>
            <Text style={styles.emergencyNumber}>1-800-222-1222</Text>
            <Text style={styles.emergencyLabel}>Poison Control</Text>
          </View>
          <View style={styles.emergencyContact}>
            <Text style={styles.emergencyNumber}>1-800-RED-CROSS</Text>
            <Text style={styles.emergencyLabel}>American Red Cross</Text>
          </View>
        </View>
        {data.local_emergency && (
          <View style={{ marginTop: 8, alignItems: 'center' }}>
            <Text style={styles.emergencyLabel}>Local Non-Emergency:</Text>
            <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#dc2626' }}>
              {data.local_emergency}
            </Text>
          </View>
        )}
      </View>

      {/* Two-column layout for procedures */}
      <View style={styles.row}>
        <View style={styles.column}>
          {emergencyProcedures.slice(0, 2).map((proc, idx) => (
            <View key={idx} style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionIcon}>{(proc as any).icon || '⚠️'}</Text>
                <Text style={styles.sectionTitle}>{proc.type.toUpperCase()}</Text>
              </View>
              <View style={styles.sectionBody}>
                {proc.immediate_actions.slice(0, 6).map((action, aidx) => (
                  <View key={aidx} style={styles.actionItem}>
                    <Text style={styles.actionNumber}>{aidx + 1}</Text>
                    <Text style={styles.actionText}>{action}</Text>
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>

        <View style={styles.column}>
          {emergencyProcedures.slice(2, 4).map((proc, idx) => (
            <View key={idx} style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionIcon}>{(proc as any).icon || '⚠️'}</Text>
                <Text style={styles.sectionTitle}>{proc.type.toUpperCase()}</Text>
              </View>
              <View style={styles.sectionBody}>
                {proc.immediate_actions.slice(0, 6).map((action, aidx) => (
                  <View key={aidx} style={styles.actionItem}>
                    <Text style={styles.actionNumber}>{aidx + 1}</Text>
                    <Text style={styles.actionText}>{action}</Text>
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Meeting Points and Utility Shutoffs */}
      <View style={styles.row}>
        <View style={{ ...styles.column, width: '48%' }}>
          <View style={{ ...styles.section, backgroundColor: '#f0fdf4' }}>
            <View style={{ ...styles.sectionHeader, backgroundColor: '#166534' }}>
              <Text style={styles.sectionIcon}>📍</Text>
              <Text style={styles.sectionTitle}>MEETING POINTS</Text>
            </View>
            <View style={styles.sectionBody}>
              <View style={styles.actionItem}>
                <Text style={{ ...styles.actionNumber, backgroundColor: '#16a34a' }}>1</Text>
                <View style={{ flex: 1 }}>
                  <Text style={{ ...styles.actionText, fontFamily: 'Helvetica-Bold' }}>Primary:</Text>
                  <Text style={styles.actionText}>
                    {data.meeting_points?.primary || '_________________'}
                  </Text>
                </View>
              </View>
              <View style={styles.actionItem}>
                <Text style={{ ...styles.actionNumber, backgroundColor: '#16a34a' }}>2</Text>
                <View style={{ flex: 1 }}>
                  <Text style={{ ...styles.actionText, fontFamily: 'Helvetica-Bold' }}>Secondary:</Text>
                  <Text style={styles.actionText}>
                    {data.meeting_points?.secondary || '_________________'}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        <View style={{ ...styles.column, width: '48%', marginRight: 0 }}>
          <View style={styles.utilityBox}>
            <Text style={styles.utilityTitle}>🔧 UTILITY SHUTOFF LOCATIONS</Text>
            <View style={styles.utilityItem}>
              <Text style={styles.utilityLabel}>⛽ Gas:</Text>
              <Text style={styles.utilityValue}>
                {data.utility_shutoffs?.gas || '_________________'}
              </Text>
            </View>
            <View style={styles.utilityItem}>
              <Text style={styles.utilityLabel}>💧 Water:</Text>
              <Text style={styles.utilityValue}>
                {data.utility_shutoffs?.water || '_________________'}
              </Text>
            </View>
            <View style={styles.utilityItem}>
              <Text style={styles.utilityLabel}>⚡ Electric:</Text>
              <Text style={styles.utilityValue}>
                {data.utility_shutoffs?.electric || '_________________'}
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* Address reminder */}
      <View style={{ backgroundColor: '#fef3c7', padding: 8, borderRadius: 4, marginTop: 8 }}>
        <Text style={{ fontSize: 8, color: '#92400e', textAlign: 'center' }}>
          <Text style={{ fontFamily: 'Helvetica-Bold' }}>Home Address: </Text>
          {data.address || '_____________________________________________'}
          {'  •  '}
          <Text style={{ fontFamily: 'Helvetica-Bold' }}>Know this for 911 calls!</Text>
        </Text>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>Prepared for: {data.name || 'Your Family'}</Text>
        <Text style={styles.footerText}>Updated: {new Date().toLocaleDateString()}</Text>
        <Text style={styles.footerText}>Review quarterly with all household members</Text>
      </View>
    </Page>
  );
};

export default QuickReference;
